import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent } from '@app/app/shared/styleguide/components/icon/icon.component';
import { LucideClock } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';

@Component({
  selector: 'ptl-time-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, TranslocoModule],
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
  ],
})
export class TimePickerComponent implements ControlValueAccessor {
  @Input() disabled = false;
  @Input() invalid = false;
  @Input() hourAriaLabel?: string;
  @Input() minuteAriaLabel?: string;

  protected readonly LucideClock = LucideClock;
  protected hour = '';
  protected minute = '';

  private onChange: (value: string | null) => void = () => undefined;
  private onTouched = () => undefined;
  private readonly cdr = inject(ChangeDetectorRef);

  writeValue(value: string | null): void {
    if (value && /^\d{1,2}:\d{1,2}$/.test(value)) {
      const [h, m] = value.split(':');
      this.hour = this.pad(this.clamp(h, 23));
      this.minute = this.pad(this.clamp(m, 59));
    } else {
      this.hour = '';
      this.minute = '';
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  protected onHourInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.hour = this.sanitize(input.value, 23);
    input.value = this.hour;
    this.emit();
  }

  protected onMinuteInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.minute = this.sanitize(input.value, 59);
    input.value = this.minute;
    this.emit();
  }

  protected onBlur(): void {
    if (this.hour !== '') {
      this.hour = this.pad(this.hour);
    }
    if (this.minute !== '') {
      this.minute = this.pad(this.minute);
    }
    this.onTouched();
    this.emit();
  }

  private emit(): void {
    if (this.hour === '' || this.minute === '') {
      this.onChange(null);
      return;
    }
    this.onChange(`${this.pad(this.hour)}:${this.pad(this.minute)}`);
  }

  private sanitize(raw: string, max: number): string {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    if (digits === '') {
      return '';
    }
    return String(Math.min(parseInt(digits, 10), max));
  }

  private clamp(raw: string, max: number): string {
    return String(Math.min(Math.max(parseInt(raw, 10) || 0, 0), max));
  }

  private pad(value: string): string {
    return value.padStart(2, '0');
  }
}
